import React, { useEffect, useState } from 'react';
import Localize from 'react-intl-universal';

import { merge } from 'lodash-es';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { ADDRESS_OPTIONS, httpStatusCodes } from '@common/Constants';
import Logger from '@common/Logger';
import { CONSENT } from '@common/network';
import { concatAddress } from '@common/Util';
import { getByPathAndParams } from '@services/BaseApi';

import ConsentManagementForm from './ConsentManagementForm';

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.booking.list,
  height: '100%',
  padding: '2rem 1rem 1rem 1rem',
  overflow: 'auto',

  [theme.breakpoints.up('xs')]: { height: 'max-content' },
  [theme.breakpoints.up('md')]: { height: 'max-content' },
  [theme.breakpoints.up('lg')]: { height: '67.3vh' }
}));

const StyledHeader = styled(Typography)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: '0.1rem',
  fontSize: '1.7rem',
  fontWeight: 400,
  color: theme.palette.booking.header
}));

const paddingBasedOnScreenSize = {
  paddingLeft: { xs: '0 !important', sm: '16px' },
  paddingRight: { xs: '5px', sm: '16px' }
};

const SingleLineInfo = ({ label, text }) => (
  <Grid
    item
    xs={12}
    sm={12}
    sx={{ display: 'flex', alignItems: 'center', gap: 1, lineHeight: '1.5rem', flexWrap: 'wrap' }}
  >
    <Typography variant='span' fontWeight={'bold'}>
      {`${label}:`}
    </Typography>
    <Typography variant='span' sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      {text}
    </Typography>
  </Grid>
);

const Checkout = ({
  contactsInfo = {},
  setIsCheckoutFormValid = () => {},
  eventId = null,
  checkedConsents,
  setCheckedConsents,
  isEventFree
}) => {
  const [listOfConsents, setListOfConsents] = useState([]);
  const generalInfo = { ...contactsInfo.generalInformation };
  const contactInfo = { ...contactsInfo.contactInformation };
  const billingAddress =
    contactsInfo.billingAddressRadio === ADDRESS_OPTIONS.noAlternative
      ? merge(generalInfo, contactInfo)
      : contactsInfo.billingAddress;

  const communicationAddress =
    contactsInfo.communicationAddressRadio === ADDRESS_OPTIONS.noAlternative
      ? merge(generalInfo, contactInfo)
      : merge(contactInfo, contactsInfo.communicationAddress);

  const fetchConsents = async () => {
    try {
      const { data, status } = await getByPathAndParams({
        path: CONSENT.GET_ALL_CONSENTS,
        params: {
          allLanguages: false,
          filters: {
            advancedFilters: [
              { key: 'type', value: 'CONSENT', operation: 'equal' },
              { key: 'eventId', value: eventId, operation: 'equal' }
            ]
          }
        },
        temApi: true
      });
      if (status === httpStatusCodes.ok) {
        return data?.content ?? [];
      }
    } catch (err) {
      Logger.error(err);
    }
    return [];
  };

  useEffect(() => {
    const loadConsents = async () => {
      const consents = await fetchConsents();
      consents.sort((a, b) => a?.orderNumber - b?.orderNumber);
      setListOfConsents(consents);
      const initialChecked = consents?.reduce((acc, consent) => {
        acc[consent.id] = false;
        return acc;
      }, {});
      setCheckedConsents(initialChecked);
    };
    loadConsents();
  }, []);

  return (
    <StyledBox>
      {!isEventFree && (
        <>
          <Grid
            item
            xs={12}
            sm={12}
            sx={{ ...paddingBasedOnScreenSize, mb: '0px !important', pt: '0px !important' }}
          >
            <StyledHeader>{Localize.get('Booking.billingAddressRecipient')}</StyledHeader>
          </Grid>
          <SingleLineInfo
            label={Localize.get('Booking.recipient')}
            text={`${billingAddress.firstName} ${billingAddress.lastName}`}
          />

          <SingleLineInfo label={Localize.get('Booking.company')} text={billingAddress.company} />
          <SingleLineInfo
            label={Localize.get('EventDetails.address')}
            text={`${billingAddress.street} , ${billingAddress.number} , ${billingAddress.city}`}
          />
          <SingleLineInfo
            label={Localize.get('Booking.emailAddress')}
            text={billingAddress.email}
          />
          <SingleLineInfo label={Localize.get('Booking.mobile')} text={billingAddress.mobile} />
        </>
      )}

      <StyledHeader sx={{ pt: 3 }}>
        {Localize.get('Booking.communicationAddressHeader')}
      </StyledHeader>
      <SingleLineInfo
        label={Localize.get('Booking.recipient')}
        text={`${communicationAddress.firstName} ${communicationAddress.lastName}`}
      />
      <SingleLineInfo label={Localize.get('Booking.company')} text={communicationAddress.company} />
      <SingleLineInfo
        label={Localize.get('EventDetails.address')}
        text={concatAddress(communicationAddress)}
      />
      <SingleLineInfo
        label={Localize.get('Booking.emailAddress')}
        text={communicationAddress.email}
      />

      <SingleLineInfo label={Localize.get('Booking.mobile')} text={communicationAddress.mobile} />
      {!isEventFree && (
        <>
          <StyledHeader sx={{ pt: 3 }}>{Localize.get('Booking.paymentMethod')}</StyledHeader>
          <Typography>{Localize.get('Booking.byInvoice')}</Typography>
        </>
      )}

      <StyledHeader sx={{ pt: 3, pb: 1 }}>{Localize.get('Booking.consentManagement')}</StyledHeader>

      <ConsentManagementForm
        setIsCheckoutFormValid={setIsCheckoutFormValid}
        listOfConsents={listOfConsents}
        checkedConsents={checkedConsents}
        setCheckedConsents={setCheckedConsents}
      />
    </StyledBox>
  );
};
export default Checkout;
